import { createContext, useContext, useSyncExternalStore } from "react";
import { clone } from "./graph";
import type { Graph, Meta } from "./types";

export interface Selection { nodes: string[]; edges: string[] }

export interface BuilderState {
  graph: Graph;
  meta: Meta;
  /** Graph + meta as last committed (or loaded): what "dirty" is measured against. */
  saved: string;
  selection: Selection;
  past: Snapshot[];
  future: Snapshot[];
  readOnly: boolean;
  dirty: boolean;
}

interface Snapshot { graph: Graph; meta: Meta; label: string }

const LIMIT = 200;
const COALESCE_MS = 900;

/**
 * Editing state of the builder: the graph and metadata, the canvas selection and an undo/redo history. Edits with the same coalesce key
 * in quick succession (typing a label, nudging a node) become one undo step.
 */
export function createBuilderStore(graph: Graph, meta: Meta, readOnly: boolean) {
  let state: BuilderState = { graph, meta, saved: fingerprint(graph, meta), selection: { nodes: [], edges: [] }, past: [], future: [], readOnly, dirty: false };
  const listeners = new Set<() => void>();
  let lastKey: string | null = null;
  let lastAt = 0;
  const emit = () => listeners.forEach((l) => l());
  const set = (next: Partial<BuilderState>) => {
    const merged = { ...state, ...next };
    const changed = merged.graph !== state.graph || merged.meta !== state.meta || merged.saved !== state.saved;
    state = changed ? { ...merged, dirty: fingerprint(merged.graph, merged.meta) !== merged.saved } : merged;
    emit();
  };

  return {
    getState: () => state,
    subscribe: (l: () => void) => { listeners.add(l); return () => listeners.delete(l); },
    /** Mutates a copy of the graph; <paramref name="coalesce"/> merges rapid edits of the same thing into one undo step. */
    update(label: string, fn: (g: Graph, m: Meta) => void, coalesce?: string) {
      if (state.readOnly) return;
      const g = clone(state.graph);
      const m = clone(state.meta);
      fn(g, m);
      const now = Date.now();
      const merge = coalesce !== undefined && coalesce === lastKey && now - lastAt < COALESCE_MS;
      lastKey = coalesce ?? null;
      lastAt = now;
      const past = merge ? state.past : [...state.past, { graph: state.graph, meta: state.meta, label }].slice(-LIMIT);
      set({ graph: g, meta: m, past, future: [] });
    },
    /** Replaces the graph (loading a commit, applying a merge or the Copilot's proposal) as one undoable step. */
    replace(graph: Graph, meta: Meta | null, label: string, asSaved = false) {
      lastKey = null;
      const next: Partial<BuilderState> = { graph, meta: meta ?? state.meta, future: [] };
      if (asSaved) { next.saved = fingerprint(graph, meta ?? state.meta); next.past = []; }
      else next.past = [...state.past, { graph: state.graph, meta: state.meta, label }].slice(-LIMIT);
      set(next);
    },
    markSaved() { set({ saved: fingerprint(state.graph, state.meta) }); },
    setReadOnly(readOnly: boolean) { set({ readOnly }); },
    select(selection: Selection) {
      if (sameSelection(selection, state.selection)) return;
      set({ selection });
    },
    undo() {
      const prev = state.past.at(-1);
      if (!prev) return;
      lastKey = null;
      set({ graph: prev.graph, meta: prev.meta, past: state.past.slice(0, -1), future: [{ graph: state.graph, meta: state.meta, label: prev.label }, ...state.future] });
    },
    redo() {
      const next = state.future[0];
      if (!next) return;
      lastKey = null;
      set({ graph: next.graph, meta: next.meta, future: state.future.slice(1), past: [...state.past, { graph: state.graph, meta: state.meta, label: next.label }] });
    },
  };
}

export type BuilderStore = ReturnType<typeof createBuilderStore>;

export function fingerprint(graph: Graph, meta: Meta) {
  return JSON.stringify({ g: graph, m: { name: meta.name, nameAr: meta.nameAr, description: meta.description ?? null, legalBasis: meta.legalBasis ?? null } });
}

const sameSelection = (a: Selection, b: Selection) => a.nodes.join() === b.nodes.join() && a.edges.join() === b.edges.join();

export const BuilderContext = createContext<BuilderStore | null>(null);

export function useStore(): BuilderStore {
  const store = useContext(BuilderContext);
  if (!store) throw new Error("useStore must be used inside the builder");
  return store;
}

export function useBuilder<T>(selector: (s: BuilderState) => T): T {
  const store = useStore();
  return useSyncExternalStore(store.subscribe, () => selector(store.getState()));
}

export const useDirty = () => useBuilder((s) => s.dirty);
